import { useEffect, useState } from 'react'
import {
  Box,
  Chip,
  Stack,
  Typography,
} from '@mui/material'
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded'
import { adminService, type AuditEntry } from '../../services/adminService'
import { EmptyState, LoadingState } from '../../components/ui/States'
import { apiError } from '../errors'
import { useToast } from '../useToast'

const ACTION_COLORS: Record<string, { color: string; bg: string }> = {
  create: { color: '#2e7d32', bg: 'rgba(46,125,50,0.12)' },
  update: { color: '#0075BC', bg: 'rgba(0,117,188,0.12)' },
  publish: { color: '#2e7d32', bg: 'rgba(46,125,50,0.12)' },
  unpublish: { color: '#B5611A', bg: 'rgba(228,119,32,0.15)' },
  clone: { color: '#3E9BA1', bg: 'rgba(91,198,204,0.18)' },
  delete: { color: '#c62828', bg: 'rgba(198,40,40,0.12)' },
}

function formatWhen(iso?: string): string {
  if (!iso) return ''
  return new Date(iso).toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

export default function AuditManager({ eventId }: { eventId: string }) {
  const { notify } = useToast()
  const [entries, setEntries] = useState<AuditEntry[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    adminService
      .listAudit(eventId)
      .then((r) => setEntries(r.entries))
      .catch((e) => notify(apiError(e), 'error'))
      .finally(() => setLoading(false))
  }, [eventId, notify])

  if (loading) return <LoadingState />
  if (entries.length === 0)
    return (
      <EmptyState
        icon={<HistoryRoundedIcon fontSize="large" />}
        title="No activity yet"
        description="Admin actions on this event (edits, publish, clone) will appear here."
      />
    )

  return (
    <Stack spacing={1}>
      {entries.map((e) => {
        const c = ACTION_COLORS[e.action] ?? ACTION_COLORS.update
        return (
          <Box
            key={e.id}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              p: 1.5,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Chip
              label={e.action}
              size="small"
              sx={{
                height: 22,
                fontWeight: 700,
                fontSize: '0.68rem',
                textTransform: 'capitalize',
                color: c.color,
                bgcolor: c.bg,
                minWidth: 74,
              }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600} noWrap>
                {e.summary || `${e.action} ${e.entityType}`}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {e.actorName || 'System'} · {formatWhen(e.occurredAt)}
              </Typography>
            </Box>
          </Box>
        )
      })}
    </Stack>
  )
}
